import { apiFetch } from '~/lib/api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers || {}) },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

export interface MeResponse {
  is_operator: boolean;
  email: string;
  domain_admin: boolean;
}

export interface FunnelResponse {
  range: '7d' | '30d' | 'all';
  totals: { applications: number; drafts: number; queued: number; sent: number; replied: number; interviews: number; rejected: number };
  rates: { reply_rate: number; interview_rate: number; gate_pass_rate: number };
  trend: Array<{ date: string; sent: number; replied: number }>;
}

export interface QualityResponse {
  range: '7d' | '30d';
  bounce_rate: number;
  gate_failure_rate: number;
  paused_users: Array<{ email: string; paused_reason: string; paused_at: string }>;
  token_cost: { total_input: number; total_output: number; est_usd: number };
}

export interface WorkersResponse {
  workers: Array<{
    name: string;
    health: 'ok' | 'stale' | 'error' | 'unknown';
    last_run_at: string | null;
    last_status: 'ok' | 'error' | null;
    duration_ms: number;
    items_processed: number;
    items_failed: number;
    last_error: string | null;
    stale_threshold_ms: number;
  }>;
}

export interface Member {
  _id: string;
  email: string;
  name?: string;
  status: 'ACTIVE' | 'PAUSED' | 'DISCONNECTED';
  role: 'USER' | 'OPERATOR';
  plan: 'STARTER' | 'PRO' | 'PREMIUM' | null;
  expires_at: string | null;
  daily_limit: number;
  visa_types: Array<'h2b' | 'h2a_jo'>;
  last_sent_at: string | null;
  paused_reason: string | null;
  sent_today: number;
  sent_total: number;
  replied: number;
  interviews: number;
}

export interface Posting {
  _id: string;
  title: string;
  company: string;
  location: string;
  source: 'static_csv' | 'dol_h2b' | 'dol_h2a' | 'manual';
  status: 'ACTIVE' | 'ARCHIVED';
  case_number: string | null;
  assigned_count: number;
  application_count: number;
  gate_pass_rate: number | null;
  created_at: string;
}

// All admin routes are mounted under the /job-apply prefix in conversations-service
// (app.use('/job-apply', jobApplyRouter) → router.use('/api/admin', adminRouter))
const BASE = '/job-apply/api/admin';

export const adminApi = {
  me: () => request<MeResponse>(`${BASE}/me`),
  funnel: (range: '7d' | '30d' | 'all' = '7d') => request<FunnelResponse>(`${BASE}/metrics/funnel?range=${range}`),
  quality: (range: '7d' | '30d' = '7d') => request<QualityResponse>(`${BASE}/quality?range=${range}`),
  workers: () => request<WorkersResponse>(`${BASE}/workers`),

  listMembers: (params: URLSearchParams) => request<{ page: number; total: number; members: Member[] }>(`${BASE}/members?${params}`),
  getMember: (id: string) => request<{ member: any; recent_applications: any[] }>(`${BASE}/members/${id}`),
  patchMember: (id: string, body: { status?: string; daily_limit?: number; paused_reason?: string; auto_send?: boolean }) =>
    request(`${BASE}/members/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  setRole: (id: string, role: 'USER' | 'OPERATOR') =>
    request(`${BASE}/members/${id}/role`, { method: 'POST', body: JSON.stringify({ role }) }),
  comp: (id: string, plan: string, months: number, note?: string) =>
    request(`${BASE}/members/${id}/comp`, { method: 'POST', body: JSON.stringify({ plan, months, note }) }),
  setPeriod: (id: string, body: { started_at?: string | null; expires_at?: string | null }) =>
    request(`${BASE}/members/${id}/period`, { method: 'PATCH', body: JSON.stringify(body) }),
  checkoutLink: (id: string, plan: string) =>
    request<{ url: string }>(`${BASE}/members/${id}/checkout-link`, { method: 'POST', body: JSON.stringify({ plan }) }),
  createOperator: (email: string) =>
    request(`${BASE}/operators`, { method: 'POST', body: JSON.stringify({ email }) }),

  listPostings: (params: URLSearchParams) => request<{ page: number; total: number; postings: Posting[] }>(`${BASE}/postings?${params}`),
  getPosting: (id: string) => request<{ posting: any; recent_applications: any[] }>(`${BASE}/postings/${id}`),
  patchPosting: (id: string, body: { status?: string; assigned_members?: string[] }) =>
    request(`${BASE}/postings/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  syncDol: () => request(`${BASE}/sync-dol`, { method: 'POST' }),
};
